import { useEffect, useState } from "react";
import { Plus, Trash2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { SERVICE_CATEGORIES } from "@/lib/pricingUtils";
import { ZONES } from "@/constants/testIds";

/** Card de Configuración: catalogo administrable de zonas de aplicacion
 * (frente, entrecejo, linea mandibular, etc.), usadas al registrar
 * tratamientos. Sugeridas por categoria de servicio como fallback cuando el
 * servicio no tiene sus propias zonas sugeridas (ver Servicios). */
export default function ZoneCatalogSettingsCard() {
  const [zones, setZones] = useState([]);
  const [edits, setEdits] = useState({});
  const [savingId, setSavingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [newName, setNewName] = useState("");
  const [newCategories, setNewCategories] = useState([]);
  const [addingOpen, setAddingOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  const load = async () => {
    const { data } = await api.get("/zones");
    setZones(data);
    setEdits({});
  };

  useEffect(() => { load(); }, []);

  const getEdit = (zone) => edits[zone.id] || { name: zone.name, categories: zone.categories || [] };
  const isDirty = (zone) => {
    const e = getEdit(zone);
    return e.name !== zone.name || JSON.stringify([...e.categories].sort()) !== JSON.stringify([...(zone.categories || [])].sort());
  };

  const setEdit = (zoneId, patch) => setEdits((prev) => ({ ...prev, [zoneId]: { ...(prev[zoneId] || zones.find((z) => z.id === zoneId)), ...patch } }));

  const toggleEditCategory = (zone, category) => {
    const current = getEdit(zone).categories;
    const next = current.includes(category) ? current.filter((c) => c !== category) : [...current, category];
    setEdit(zone.id, { categories: next });
  };

  const handleSaveRow = async (zone) => {
    const edit = getEdit(zone);
    setSavingId(zone.id);
    try {
      await api.put(`/zones/${zone.id}`, { name: edit.name, categories: edit.categories });
      toast({ title: "Zona actualizada" });
      load();
    } catch (err) {
      toast({ title: "Error al guardar", description: formatApiErrorDetail(err.response?.data?.detail), variant: "destructive" });
    } finally {
      setSavingId(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/zones/${deleteTarget.id}`);
      toast({ title: "Zona eliminada" });
      setDeleteTarget(null);
      load();
    } catch (err) {
      toast({ title: "Error al eliminar", description: formatApiErrorDetail(err.response?.data?.detail), variant: "destructive" });
    }
  };

  const toggleNewCategory = (category) => {
    setNewCategories((prev) => (prev.includes(category) ? prev.filter((c) => c !== category) : [...prev, category]));
  };

  const handleAdd = async () => {
    if (!newName.trim()) return;
    setSaving(true);
    try {
      await api.post("/zones", { name: newName.trim(), categories: newCategories });
      toast({ title: "Zona agregada" });
      setNewName("");
      setNewCategories([]);
      setAddingOpen(false);
      load();
    } catch (err) {
      toast({ title: "Error al agregar", description: formatApiErrorDetail(err.response?.data?.detail), variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white border border-border rounded-xl p-6 sm:p-8 shadow-sm space-y-5" data-testid={ZONES.card}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="font-serif text-xl text-foreground">Zonas de aplicación</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Catálogo usado al registrar tratamientos (ficha técnica). Las categorías se usan como sugerencia cuando el servicio no tiene zonas propias configuradas.
          </p>
        </div>
        {!addingOpen && (
          <Button variant="outline" size="sm" onClick={() => setAddingOpen(true)} data-testid={ZONES.addButton}>
            <Plus className="w-3.5 h-3.5" /> Agregar zona
          </Button>
        )}
      </div>

      {addingOpen && (
        <div className="border border-border rounded-lg p-4 space-y-3 bg-muted/30">
          <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Nombre de la zona (ej: Bandas platismales)" data-testid={ZONES.newNameInput} />
          <div className="flex gap-4 flex-wrap">
            {SERVICE_CATEGORIES.map((c) => (
              <label key={c} className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox checked={newCategories.includes(c)} onCheckedChange={() => toggleNewCategory(c)} />
                {c}
              </label>
            ))}
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={handleAdd} disabled={saving || !newName.trim()} data-testid={ZONES.newSaveButton}>
              {saving && <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />} Guardar
            </Button>
            <Button size="sm" variant="outline" onClick={() => { setAddingOpen(false); setNewName(""); setNewCategories([]); }} data-testid={ZONES.newCancelButton}>Cancelar</Button>
          </div>
        </div>
      )}

      {zones.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid={ZONES.emptyState}>Todavía no hay zonas cargadas.</p>
      ) : (
        <div className="space-y-3">
          {zones.map((zone) => {
            const edit = getEdit(zone);
            const dirty = isDirty(zone);
            return (
              <div key={zone.id} className="border border-border rounded-lg p-3 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`${ZONES.rowPrefix}${zone.id}`}>
                <Input
                  value={edit.name}
                  onChange={(e) => setEdit(zone.id, { name: e.target.value })}
                  className="sm:w-56"
                  data-testid={`${ZONES.nameInputPrefix}${zone.id}`}
                />
                <div className="flex gap-3 flex-wrap flex-1">
                  {SERVICE_CATEGORIES.map((c) => (
                    <label key={c} className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer">
                      <Checkbox
                        checked={edit.categories.includes(c)}
                        onCheckedChange={() => toggleEditCategory(zone, c)}
                        data-testid={`${ZONES.categoryCheckboxPrefix}${zone.id}-${c}`}
                      />
                      {c}
                    </label>
                  ))}
                </div>
                <div className="flex gap-1.5 shrink-0">
                  {dirty && (
                    <Button size="sm" onClick={() => handleSaveRow(zone)} disabled={savingId === zone.id} data-testid={`${ZONES.saveButtonPrefix}${zone.id}`}>
                      Guardar
                    </Button>
                  )}
                  <Button variant="ghost" size="icon" onClick={() => setDeleteTarget(zone)} data-testid={`${ZONES.deleteButtonPrefix}${zone.id}`}>
                    <Trash2 className="w-4 h-4 text-destructive" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <AlertDialog open={Boolean(deleteTarget)} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar {deleteTarget?.name}?</AlertDialogTitle>
            <AlertDialogDescription>No afecta a los tratamientos ya registrados con esta zona, solo deja de sugerirse para nuevos.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive hover:bg-destructive/90">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
